import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import { BookOpen, ExternalLink, ArrowRight, Lock, Crown } from "lucide-react";

const audienceBadge = (a) => {
  if (a === "premium") return { icon: Crown, label: "Premium", cls: "bg-[#C5A880]/15 text-[#C5A880]" };
  if (a === "members") return { icon: Lock, label: "Membros", cls: "bg-indigo-500/15 text-indigo-300" };
  return null;
};

const Articles = () => {
  const [data, setData] = useState({ articles: [], workshop_url: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await client.get("/articles");
        setData(r.data);
        // Prefetch top 3 articles into the service-worker cache.
        // We fire-and-forget — the SW (`stale-while-revalidate` on
        // /api/articles*) will store the responses; subsequent navigation
        // becomes instant. Logged-in users skip prefetch since the SW
        // intentionally only caches anonymous GETs (avoids leaking
        // members/premium-gated bodies into the cache).
        const isAnonymous = !localStorage.getItem("sm_token");
        if (isAnonymous) {
          const top = (r.data?.articles || []).slice(0, 3);
          // Use plain fetch (no Authorization header) so the SW cache rule matches.
          top.forEach((a) => {
            try {
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/articles/${a.slug}`,
                { credentials: "omit" }).catch(() => null);
            } catch (prefetchErr) {
              // Prefetch is a nice-to-have — non-blocking. Log for debugging
              // but don't surface to the user.
              if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.warn("[articles prefetch]", prefetchErr);
              }
            }
          });
        }
      } catch (err) {
        // Surface the failure to the user via the empty state (rendered
        // when `data.articles` stays empty). Before this catch existed,
        // a CORS/network error would propagate uncaught and render a blank
        // page — which is exactly the mobile bug we saw earlier.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("[articles] failed to load:", err);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="articles-page">
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#C5A880]" />
          <span className="label-uppercase">Conhecimento</span>
        </div>
        <h1 className="font-display text-4xl md:text-6xl mt-3 mb-4">
          Antes de procurar, <em className="text-[#C5A880]">aprende a olhar</em>.
        </h1>
        <p className="text-[#DCD7D2] max-w-2xl mb-12 leading-relaxed">
          Artigos práticos para te ajudar no recrutamento amoroso — identificar bandeiras vermelhas,
          criar empatia, validar candidatos. Inspiração: Esther Perel, Dr. Ramani, Adelino Cunha.
        </p>

        {/* Workshop CTA hero */}
        {data.workshop_url && (
          <a href={data.workshop_url} target="_blank" rel="noopener noreferrer"
            className="block mb-12 p-6 md:p-8 rounded-2xl bg-gradient-to-r from-[#C5A880]/15 to-[#161920] border border-[#C5A880]/30 hover:border-[#C5A880] transition-colors group"
            data-testid="articles-workshop-cta">
            <span className="label-uppercase">Workshop em vídeo</span>
            <h2 className="font-display text-2xl md:text-3xl mt-2 mb-2 text-[#F5F2EE] group-hover:text-[#C5A880] transition-colors">
              "O que ninguém te ensinou sobre Relações" — com Adelino Cunha
            </h2>
            <p className="text-sm text-[#A29F9B] mb-4 max-w-2xl">
              Vê o workshop completo (online) que junta neurociência, psicologia e prática para reescreveres a forma como te relacionas.
            </p>
            <span className="inline-flex items-center gap-2 text-[#C5A880] text-sm">
              Aceder ao workshop <ExternalLink className="w-4 h-4" />
            </span>
          </a>
        )}

        {loading && <div className="text-[#A29F9B]">A carregar artigos...</div>}

        {!loading && data.articles.length === 0 && (
          <div className="text-center py-16 px-6 rounded-2xl bg-[#161920] border border-white/5"
               data-testid="articles-empty-state">
            <BookOpen className="w-10 h-10 text-[#C5A880] mx-auto mb-4 opacity-60" />
            <h3 className="font-display text-xl text-[#F5F2EE] mb-2">Ainda não há artigos publicados</h3>
            <p className="text-sm text-[#A29F9B] mb-5">
              Se já vês outras pessoas a ler artigos aqui, força a actualização para limpar o cache local.
            </p>
            <button onClick={() => window.location.reload(true)}
                    className="text-xs uppercase tracking-wider px-4 py-2 rounded-full border border-[#C5A880] text-[#C5A880] hover:bg-[#C5A880]/10 transition-colors"
                    data-testid="articles-empty-reload">
              Forçar actualização
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          {data.articles.map((a) => {
            const ab = audienceBadge(a.audience);
            const Icon = ab?.icon;
            return (
            <Link key={a.slug} to={`/artigos/${a.slug}`}
              className="group bg-[#161920] border border-white/5 rounded-2xl p-6 hover:border-[#C5A880]/40 transition-colors"
              data-testid={`article-card-${a.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <span className="label-uppercase">{a.category}</span>
                {ab && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${ab.cls}`}
                        data-testid={`article-audience-${a.slug}`}>
                    <Icon className="w-3 h-3" /> {ab.label}
                  </span>
                )}
              </div>
              <h3 className="font-display text-xl md:text-2xl mt-2 mb-3 text-[#F5F2EE] group-hover:text-[#C5A880] transition-colors leading-tight">
                {a.title}
              </h3>
              <p className="text-sm text-[#A29F9B] leading-relaxed mb-4">{a.summary}</p>
              <span className="inline-flex items-center gap-2 text-xs text-[#C5A880]">
                Ler artigo <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Articles;
